"use client";

import type {
  CSSProperties,
  PointerEventHandler,
  RefObject,
  MutableRefObject,
} from "react";
import { useCallback, useMemo, useRef, useState } from "react";

interface UseSwipeDismissOptions {
  onDismiss: () => void;
  distanceRatio?: number;
  minDistance?: number;
  maxDistance?: number;
  velocityThreshold?: number;
  activationDistance?: number;
  maxAngleDeg?: number;
}

interface UseSwipeDismissResult {
  cardRef: RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  shouldPreventClickRef: MutableRefObject<boolean>;
  touchAction: CSSProperties["touchAction"];
  contentStyle: CSSProperties;
  handlers: {
    onPointerDown: PointerEventHandler<HTMLDivElement>;
    onPointerMove: PointerEventHandler<HTMLDivElement>;
    onPointerUp: PointerEventHandler<HTMLDivElement>;
    onPointerCancel: PointerEventHandler<HTMLDivElement>;
  };
}

export function useSwipeDismiss({
  onDismiss,
  distanceRatio = 0.28,
  minDistance = 64,
  maxDistance = 110,
  velocityThreshold = 0.4,
  activationDistance = 10,
  maxAngleDeg = 45,
}: UseSwipeDismissOptions): UseSwipeDismissResult {
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const pointerIdRef = useRef<number | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const dismissDistanceRef = useRef(0);
  const shouldPreventClickRef = useRef(false);

  const maxSlope = Math.tan((maxAngleDeg * Math.PI) / 180);

  const getDismissDistance = useCallback(() => {
    const height = cardRef.current?.offsetHeight ?? 240;
    return Math.min(maxDistance, Math.max(minDistance, height * distanceRatio));
  }, [distanceRatio, maxDistance, minDistance]);

  const resetDragging = useCallback(() => {
    setIsDragging(false);
    setDragOffsetY(0);
    pointerIdRef.current = null;
    pointerStartRef.current = null;
  }, []);

  const onPointerDown: PointerEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) {
        return;
      }

      pointerIdRef.current = e.pointerId;
      pointerStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        time: Date.now(),
      };
      dismissDistanceRef.current = getDismissDistance();
      shouldPreventClickRef.current = false;

      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Ignore unsupported environments.
      }
    },
    [getDismissDistance],
  );

  const onPointerMove: PointerEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (pointerIdRef.current !== e.pointerId || !pointerStartRef.current) {
        return;
      }

      const deltaX = e.clientX - pointerStartRef.current.x;
      const deltaY = e.clientY - pointerStartRef.current.y;

      if (!isDragging) {
        const upwardDistance = -deltaY;
        if (upwardDistance < activationDistance) {
          return;
        }
        const slope = Math.abs(deltaX) / Math.max(upwardDistance, 1);
        if (slope > maxSlope) {
          resetDragging();
          return;
        }
        setIsDragging(true);
      }

      shouldPreventClickRef.current = true;
      setDragOffsetY(Math.min(0, deltaY));
    },
    [activationDistance, isDragging, maxSlope, resetDragging],
  );

  const onPointerUp: PointerEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (pointerIdRef.current !== e.pointerId || !pointerStartRef.current) {
        return;
      }

      const deltaX = e.clientX - pointerStartRef.current.x;
      const deltaY = e.clientY - pointerStartRef.current.y;
      const elapsed = Math.max(1, Date.now() - pointerStartRef.current.time);
      const upwardDistance = Math.max(0, -deltaY);
      const upwardVelocity = upwardDistance / elapsed;
      const slope = Math.abs(deltaX) / Math.max(upwardDistance, 1);
      const dismissDistance =
        dismissDistanceRef.current || getDismissDistance();

      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Ignore unsupported environments.
      }

      if (
        upwardDistance >= activationDistance &&
        slope <= maxSlope &&
        (upwardDistance >= dismissDistance ||
          upwardVelocity >= velocityThreshold)
      ) {
        onDismiss();
      }

      resetDragging();
    },
    [
      activationDistance,
      getDismissDistance,
      maxSlope,
      onDismiss,
      resetDragging,
      velocityThreshold,
    ],
  );

  const onPointerCancel: PointerEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      try {
        if (pointerIdRef.current !== null) {
          e.currentTarget.releasePointerCapture(pointerIdRef.current);
        }
      } catch {
        // Ignore unsupported environments.
      }
      resetDragging();
    },
    [resetDragging],
  );

  const contentStyle = useMemo<CSSProperties>(
    () => ({
      transform: `translateY(${dragOffsetY}px)`,
      opacity:
        1 - Math.min(Math.abs(dragOffsetY) / getDismissDistance(), 1) * 0.6,
      transition: isDragging ? "none" : undefined,
    }),
    [dragOffsetY, getDismissDistance, isDragging],
  );

  return {
    cardRef,
    isDragging,
    shouldPreventClickRef,
    touchAction: "none",
    contentStyle,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
  };
}
